import { Link } from 'react-router-dom'
import styled, { keyframes } from 'styled-components'

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`

export const FindTherapistWrap = styled.section`
  width: 100%;
  position: relative;
  overflow: hidden;
  background: linear-gradient(165deg, #1a1625 0%, #2d2438 40%, #1f1b24 100%);
  font-family: 'Outfit', -apple-system, BlinkMacSystemFont, sans-serif;
  padding: 56px 20px 64px;

  &::before {
    content: '';
    position: absolute;
    top: -20%;
    right: -15%;
    width: 60%;
    max-width: 480px;
    height: 80%;
    background: radial-gradient(
      ellipse at center,
      rgba(113, 84, 176, 0.15) 0%,
      rgba(92, 61, 138, 0.06) 40%,
      transparent 70%
    );
    pointer-events: none;
  }
  &::after {
    content: '';
    position: absolute;
    bottom: -10%;
    left: -10%;
    width: 40%;
    max-width: 320px;
    height: 50%;
    background: radial-gradient(
      ellipse at center,
      rgba(113, 84, 176, 0.08) 0%,
      transparent 60%
    );
    pointer-events: none;
  }

  @media (min-width: 768px) {
    padding: 72px 40px 88px;
  }
  @media (min-width: 1024px) {
    padding: 88px 56px 100px;
  }
`

export const FindTherapistInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 20px;
  @media (min-width: 768px) {
    gap: 24px;
  }
  @media (min-width: 1024px) {
    gap: 28px;
  }
`

export const FindTherapistEyebrow = styled.span`
  display: inline-block;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 14px;
  animation: ${fadeInUp} 0.5s ease-out;
`

export const FindTherapistHeadline = styled.h2`
  margin: 0 0 16px 0;
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.3;
  letter-spacing: -0.03em;
  color: #ffffff;
  max-width: 560px;
  animation: ${fadeInUp} 0.5s ease-out 0.05s both;
  @media (min-width: 768px) {
    font-size: 2rem;
    margin-bottom: 20px;
    max-width: 640px;
  }
  @media (min-width: 1024px) {
    font-size: 2.25rem;
  }
`

export const FindTherapistDescription = styled.p`
  margin: 0 0 28px 0;
  font-size: 0.95rem;
  font-weight: 400;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.82);
  max-width: 520px;
  animation: ${fadeInUp} 0.5s ease-out 0.1s both;
  @media (min-width: 768px) {
    font-size: 1.05rem;
    margin-bottom: 36px;
    max-width: 560px;
  }
`

export const FindTherapistCTA = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 14px 28px;
  font-size: 0.9rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  text-decoration: none;
  color: #ffffff;
  background: linear-gradient(135deg, #8b6cb8 0%, #7154b0 50%, #5c3d8a 100%);
  border: none;
  border-radius: 14px;
  cursor: pointer;
  box-shadow: 0 4px 20px rgba(113, 84, 176, 0.4);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  animation: ${fadeInUp} 0.5s ease-out 0.15s both;

  &:hover {
    color: #ffffff;
    transform: translateY(-2px);
    box-shadow: 0 8px 28px rgba(113, 84, 176, 0.5);
  }
  &:active {
    transform: translateY(0);
  }
  @media (min-width: 768px) {
    padding: 16px 32px;
    font-size: 0.95rem;
  }
`

export const FindTherapistCardsWrap = styled.div`
  margin-top: 40px;
  position: relative;
  @media (min-width: 768px) {
    margin-top: 48px;
  }
`

export const FindTherapistCardsScroll = styled.div`
  display: flex;
  gap: 16px;
  overflow-x: auto;
  padding: 8px 0 20px;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  animation: ${fadeInUp} 0.5s ease-out 0.2s both;

  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.06);
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(113, 84, 176, 0.4);
    border-radius: 3px;
  }
  @media (min-width: 768px) {
    gap: 20px;
    padding: 12px 0 24px;
  }
`

export const FindTherapistCard = styled(Link)`
  flex: 0 0 160px;
  width: 160px;
  scroll-snap-align: start;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-decoration: none;
  color: inherit;
  border-radius: 20px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-4px);
    border-color: rgba(113, 84, 176, 0.5);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.3);
  }
  @media (min-width: 768px) {
    flex: 0 0 200px;
    width: 200px;
  }
`

export const FindTherapistCardImage = styled.div`
  width: 100%;
  aspect-ratio: 3/4;
  background: rgba(255, 255, 255, 0.08);
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`

export const FindTherapistCardName = styled.span`
  display: block;
  width: 100%;
  padding: 14px 12px;
  font-size: 0.9rem;
  font-weight: 600;
  color: #ffffff;
  text-align: center;
  line-height: 1.3;
  background: rgba(255, 255, 255, 0.04);
  @media (min-width: 768px) {
    padding: 16px 14px;
    font-size: 1rem;
  }
`

export const FindTherapistCardTag = styled.span`
  display: block;
  font-size: 0.75rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.6);
  margin-top: 2px;
`

export const FindTherapistEmpty = styled.p`
  margin: 32px 0 0 0;
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.6);
  text-align: center;
  animation: ${fadeInUp} 0.5s ease-out 0.2s both;
`
